import {
  assigneeFromConversation,
  contactIdFromConversation,
  contactNameFromConversation,
  lastMessagePreview,
} from './boardChatUtils';

export const EMPTY_RESOLVED_FILTERS = {
  period: null,
  protocol: '',
  userIds: [],
  text: '',
  assigneeIds: [],
};

function toMs(ts) {
  if (ts == null || ts === '') return null;
  if (typeof ts === 'number') {
    if (!Number.isFinite(ts)) return null;
    return ts < 1e12 ? ts * 1000 : ts;
  }
  const raw = String(ts).trim();
  if (/^\d+(\.\d+)?$/.test(raw)) {
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return n < 1e12 ? n * 1000 : n;
  }
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

export function conversationActivityMs(conv) {
  return (
    toMs(conv?.last_non_activity_message?.created_at) ||
    toMs(conv?.timestamp) ||
    toMs(conv?.last_activity_at) ||
    toMs(conv?.created_at) ||
    null
  );
}

function parseYmdStart(ymd) {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}

function parseYmdEnd(ymd) {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
}

export function conversationSearchBlob(conv) {
  const name = contactNameFromConversation(conv);
  const preview = lastMessagePreview(conv);
  const id = String(conv?.id ?? '');
  return `${id} ${name} ${preview}`.toLowerCase();
}

export function countResolvedFilters(filters = EMPTY_RESOLVED_FILTERS) {
  let n = 0;
  if (filters.period?.startDate && filters.period?.endDate) n += 1;
  if (String(filters.protocol || '').trim()) n += 1;
  if ((filters.userIds || []).length) n += filters.userIds.length;
  if (String(filters.text || '').trim()) n += 1;
  if ((filters.assigneeIds || []).length) n += filters.assigneeIds.length;
  return n;
}

export function applyResolvedFilters(conversations, filters = EMPTY_RESOLVED_FILTERS) {
  const list = Array.isArray(conversations) ? conversations : [];
  const protocol = String(filters.protocol || '').trim();
  const text = String(filters.text || '').trim().toLowerCase();
  const userIds = new Set((filters.userIds || []).map(String));
  const assigneeIds = new Set((filters.assigneeIds || []).map(String));
  const startMs = filters.period?.startDate ? parseYmdStart(filters.period.startDate) : null;
  const endMs = filters.period?.endDate ? parseYmdEnd(filters.period.endDate) : null;

  return list.filter((c) => {
    if (protocol) {
      const id = String(c?.id ?? '');
      if (!id.includes(protocol)) return false;
    }

    if (userIds.size > 0) {
      const cid = contactIdFromConversation(c);
      if (cid == null || !userIds.has(String(cid))) return false;
    }

    if (assigneeIds.size > 0) {
      const a = assigneeFromConversation(c);
      const aid = a?.id ?? a?.user_id;
      if (aid == null || !assigneeIds.has(String(aid))) return false;
    }

    if (startMs != null || endMs != null) {
      const ms = conversationActivityMs(c);
      if (ms == null) return false;
      if (startMs != null && ms < startMs) return false;
      if (endMs != null && ms > endMs) return false;
    }

    if (text && !conversationSearchBlob(c).includes(text)) return false;

    return true;
  });
}

export function uniqueContactsFromConversations(conversations) {
  const map = new Map();
  for (const c of conversations || []) {
    const id = contactIdFromConversation(c);
    if (id == null) continue;
    const key = String(id);
    if (map.has(key)) continue;
    map.set(key, {
      value: key,
      label: contactNameFromConversation(c),
    });
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

export function uniqueAssigneesFromConversations(conversations) {
  const map = new Map();
  for (const c of conversations || []) {
    const a = assigneeFromConversation(c);
    const id = a?.id ?? a?.user_id;
    if (id == null) continue;
    const key = String(id);
    if (map.has(key)) continue;
    const name = String(a.name || a.available_name || `#${key}`).trim();
    map.set(key, { value: key, label: name });
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

export function searchConversationsForText(conversations, term) {
  const q = String(term || '').trim().toLowerCase();
  if (!q) return [];
  return (conversations || [])
    .filter((c) => conversationSearchBlob(c).includes(q))
    .slice(0, 40)
    .map((c) => ({
      value: String(c.id),
      label: contactNameFromConversation(c),
      hint: lastMessagePreview(c),
      conversation: c,
    }));
}

export function periodLabel(period) {
  if (!period?.startDate || !period?.endDate) return '';
  const fmt = (ymd) => {
    const [y, m, d] = String(ymd).split('-');
    if (!y || !m || !d) return ymd;
    return `${d}/${m}/${y}`;
  };
  return `${fmt(period.startDate)} — ${fmt(period.endDate)}`;
}
